import type { UserRole } from '@today/types'
import type { AppSidebarGroup, NavigationPredicate } from '@today/ui-components'
import type { NavigationPredicate as PredicateName } from './config.js'
import { navPredicateHolds } from './filterNav.js'

/**
 * The workspace (sell-flow) plane's sidebar (TO-636, AD-4), rendered by
 * `AppSidebar` inside `WorkspaceShell`. Items gate on permission atoms through
 * the shared predicate names of `config.ts`; an item with no predicate shows to
 * every authenticated viewer.
 *
 * The "Admin" group is transitional: it keeps today's links to non-workspace
 * screens (admin console, locale settings, content governance — AD-7 Tenant
 * Admin) reachable until the Platform / Tenant Admin shells (W1-2 / W1-3)
 * re-home them.
 */
export const WORKSPACE_NAV_GROUPS: AppSidebarGroup[] = [
	{ key: 'overview', items: [{ label: 'Overview', target: '/dashboard' }] },
	{
		key: 'sell',
		label: 'Sell',
		items: [
			{ label: 'Customers', target: '/customers', visibleIf: 'can-read-customers' },
			{ label: 'Offers', target: '/offers', visibleIf: 'can-read-offers' },
		],
	},
	{
		key: 'catalog',
		label: 'Catalog',
		items: [
			{ label: 'Products', target: '/products', visibleIf: 'can-read-products' },
			{ label: 'Product categories', target: '/product-categories', visibleIf: 'can-read-products' },
			{ label: 'Texts', target: '/content/texts', visibleIf: 'can-read-content' },
		],
	},
	{
		key: 'admin',
		label: 'Admin',
		items: [
			{ label: 'Admin console', target: '/admin', visibleIf: 'can-open-admin' },
			{ label: 'Locale settings', target: '/admin/locale-settings', visibleIf: 'can-manage-locales' },
			{ label: 'Approval inbox', target: '/content/inbox', visibleIf: 'can-approve-content' },
			{ label: 'Content types', target: '/content/types', visibleIf: 'can-manage-content-types' },
			{ label: 'Tenant defaults', target: '/content/tenant-defaults', visibleIf: 'can-read-content' },
		],
	},
]

const PREDICATE_NAMES: readonly PredicateName[] = [
	'can-read-customers',
	'can-read-offers',
	'can-read-products',
	'can-read-content',
	'can-open-admin',
	'can-manage-locales',
	'can-approve-content',
	'can-manage-content-types',
]

/**
 * `AppSidebar`'s predicate map for `role`. Every evaluator delegates to
 * `navPredicateHolds` (red-team RT-001), so the workspace nav and the legacy
 * sidebar share one atom-to-link rule set. `role` is needed only by
 * `can-open-admin`, whose existing rule admits a Super-Admin outright.
 */
export function workspaceNavPredicates(role: UserRole): Record<string, NavigationPredicate> {
	return Object.fromEntries(
		PREDICATE_NAMES.map((name) => [
			name,
			(permissions: readonly string[]) => navPredicateHolds(name, { role, permissions }),
		]),
	)
}

/** Whether `pathname` is `target` itself or a page beneath it (whole segments only). */
function isWithin(pathname: string, target: string): boolean {
	return pathname === target || pathname.startsWith(`${target}/`)
}

/**
 * The nav target to mark current for `pathname`: the longest target the path is
 * within, so `/customers/42/edit` keeps Customers current and
 * `/product-categories` never matches Products. `null` when no target matches
 * (e.g. `/settings`). `AppSidebar` marks an item current on exact equality, so
 * the shell passes this as its `currentPath`.
 */
export function activeNavTarget(pathname: string, groups: readonly AppSidebarGroup[]): string | null {
	const matches = groups
		.flatMap((group) => group.items.map((item) => item.target))
		.filter((target) => isWithin(pathname, target))
	if (matches.length === 0) return null
	return matches.reduce((longest, target) => (target.length > longest.length ? target : longest))
}
